import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSecurityStore } from "../store/security-store";
import { useLoginHistory } from "@/api/hooks/use-login-history";
import { queryService } from "@/api/services/query-service";
import { useToast } from "@/features/toast";

export const useSecurity = () => {
  const { t } = useTranslation();
  const { success, error } = useToast();

  // Store Hook
  const {
    // Password Form States
    currentPassword,
    newPassword,
    confirmPassword,
    showCurrentPassword,
    showNewPassword,
    showConfirmPassword,

    // Password Form Actions
    setCurrentPassword,
    setNewPassword,
    setConfirmPassword,
    toggleShowCurrentPassword,
    toggleShowNewPassword,
    toggleShowConfirmPassword,
    resetPasswordForm,

    // Login History Management States
    loginHistory,
    isLoginHistoryLoading,
    loginHistoryTotal,

    // Login History Management Actions
    setLoginHistory,
    setLoginHistoryLoading,
    setLoginHistoryTotal,
  } = useSecurityStore();

  // API Hook - Login History (Infinite Query)
  const {
    data: loginHistoryData,
    isLoading: isLoginHistoryFetching,
    error: loginHistoryError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchLoginHistory,
  } = useLoginHistory({
    limit: 10,
    sortBy: "-createdAt",
  });

  // Sync login history data from API to store
  useEffect(() => {
    if (loginHistoryData?.pages) {
      // Tüm sayfalardaki result'ları birleştir
      const allResults = loginHistoryData.pages.flatMap(
        (page) => page.result || []
      );
      const total = loginHistoryData.pages[0]?.meta?.total || 0;

      setLoginHistory(allResults);
      setLoginHistoryTotal(total);
    }
  }, [loginHistoryData, setLoginHistory, setLoginHistoryTotal]);

  // Sync loading state
  useEffect(() => {
    setLoginHistoryLoading(isLoginHistoryFetching);
  }, [isLoginHistoryFetching, setLoginHistoryLoading]);

  // Business Actions
  const handleChangePassword = async () => {
    try {
      // Validation: Check if passwords match
      if (newPassword !== confirmPassword) {
        error(t("settings.password.passwordsDoNotMatch"));
        return;
      }

      // API Call: Change password
      const response = await queryService.changePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
      });

      // Success handling
      if (response.success) {
        success(t("settings.password.changePasswordSuccess"));
        resetPasswordForm();
      } else {
        error(response.message || t("settings.password.changePasswordError"));
      }
    } catch (errorResponse: any) {
      console.error("Failed to change password:", errorResponse);

      // Handle specific error cases
      const errorMessage = errorResponse?.response?.data?.message;
      if (errorMessage?.includes("current password")) {
        error(t("settings.password.invalidCurrentPassword"));
      } else {
        error(errorMessage || t("settings.password.changePasswordError"));
      }
    }
  };

  // Business Actions - Login History (Infinite Scroll)
  const handleLoadMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleRefreshLoginHistory = useCallback(async () => {
    await refetchLoginHistory();
  }, [refetchLoginHistory]);

  // Computed values
  const hasPasswordChanges =
    currentPassword !== "" || newPassword !== "" || confirmPassword !== "";
  const isPasswordFormValid =
    currentPassword !== "" &&
    newPassword !== "" &&
    confirmPassword !== "" &&
    newPassword === confirmPassword;

  return {
    // Password Form Data
    currentPassword,
    newPassword,
    confirmPassword,
    showCurrentPassword,
    showNewPassword,
    showConfirmPassword,
    hasPasswordChanges,
    isPasswordFormValid,

    // Password Form Actions
    setCurrentPassword,
    setNewPassword,
    setConfirmPassword,
    toggleShowCurrentPassword,
    toggleShowNewPassword,
    toggleShowConfirmPassword,
    resetPasswordForm,
    handleChangePassword,

    // Login History Data
    loginHistory,
    isLoginHistoryLoading,
    loginHistoryTotal,
    loginHistoryError,

    // Login History Infinite Scroll
    hasNextPage,
    isFetchingNextPage,

    // Login History Actions
    handleLoadMore,
    handleRefreshLoginHistory,
  };
};
